import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useAuth } from "../App";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { Settings, Zap, Loader2, PlayCircle, CheckCircle2, AlertTriangle, FileText, Download, RefreshCw, TrendingUp, History, FileSpreadsheet, DatabaseBackup, ShieldAlert, Clock, Mail, Trash2 } from "lucide-react";

const fmtBs = (n) => `Bs. ${(Number(n) || 0).toLocaleString("es-VE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const statusMeta = {
  simulado: { color: "bg-blue-100 text-blue-800", label: "Simulado" },
  creada: { color: "bg-emerald-100 text-emerald-800", label: "Creada" },
  al_dia: { color: "bg-green-100 text-green-800", label: "Al día" },
  sin_tarifa: { color: "bg-amber-100 text-amber-800", label: "Sin tarifa" },
  error: { color: "bg-rose-100 text-rose-800", label: "Error" },
};

export default function ConfiguracionPage() {
  const { token, API, user, hasPermission } = useAuth();
  const auth = useMemo(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);
  const [form, setForm] = useState(null);

  // ==== Acciones masivas ====
  const [runningMasiva, setRunningMasiva] = useState(false);
  const [resultado, setResultado] = useState(null);
  const [showResultado, setShowResultado] = useState(false);
  const [modoResultado, setModoResultado] = useState("preview"); // 'preview' | 'final'
  // ==== Actualización de Tasa de Liquidación ====
  const [runningTasa, setRunningTasa] = useState(false);
  const [resTasa, setResTasa] = useState(null);
  const [showTasa, setShowTasa] = useState(false);
  const [modoTasa, setModoTasa] = useState("preview");
  const [fechaTasa, setFechaTasa] = useState(new Date().toISOString().slice(0, 10));
  // ==== Historial de acciones masivas ====
  const [historial, setHistorial] = useState([]);
  const [loadingHist, setLoadingHist] = useState(false);
  const [filtroTipo, setFiltroTipo] = useState("");
  const [filtroDesde, setFiltroDesde] = useState("");
  const [filtroHasta, setFiltroHasta] = useState("");
  // ==== Respaldo (Backup) DB ====
  const [runningBackup, setRunningBackup] = useState(false);
  const [ultimoBackup, setUltimoBackup] = useState(null); // { filename, size, at }
  const [scheduleInfo, setScheduleInfo] = useState(null); // datos del scheduler
  const [loadingSchedule, setLoadingSchedule] = useState(false);
  const [runningScheduled, setRunningScheduled] = useState(false);

  const loadScheduleInfo = async () => {
    if (user?.role !== "superadmin") return;
    setLoadingSchedule(true);
    try {
      const r = await axios.get(`${API}/configuracion/backup/schedule`, auth);
      setScheduleInfo(r.data);
    } catch { /* ignore */ }
    finally { setLoadingSchedule(false); }
  };

  const dispararBackupProgramado = async () => {
    if (!window.confirm("Se generará un respaldo, se guardará en el servidor y se enviará por email a los Superadmin. ¿Continuar?")) return;
    setRunningScheduled(true);
    try {
      const r = await axios.post(`${API}/configuracion/backup/run-now`, {}, auth);
      if (r.data.status === "ok") {
        const emailMsg = r.data.email_status === "enviado" ? `Email enviado a ${(r.data.email_destinos || []).length} superadmin.`
                       : r.data.email_status === "sin_api_key" ? "Guardado en servidor. Email deshabilitado (RESEND_API_KEY vacía)."
                       : r.data.email_status === "sin_destinatarios" ? "Guardado en servidor. No hay superadmins con email."
                       : `Guardado. Email: ${r.data.email_status}.`;
        toast.success(`Respaldo generado: ${r.data.filename}. ${emailMsg}`);
      } else {
        toast.error(`Error en el respaldo: ${r.data.error || "Desconocido"}`);
      }
      loadScheduleInfo();
    } catch (e) { toast.error(e.response?.data?.detail || "Error al disparar el respaldo"); }
    finally { setRunningScheduled(false); }
  };

  const descargarStored = async (filename) => {
    try {
      const r = await axios.get(`${API}/configuracion/backup/stored/${filename}`, { ...auth, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url; a.download = filename; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("No se pudo descargar"); }
  };

  const eliminarStored = async (filename) => {
    if (!window.confirm(`¿Eliminar el respaldo "${filename}" del servidor? Esta acción es irreversible.`)) return;
    try {
      await axios.delete(`${API}/configuracion/backup/stored/${filename}`, auth);
      toast.success("Respaldo eliminado");
      loadScheduleInfo();
    } catch { toast.error("No se pudo eliminar"); }
  };

  const descargarBackup = async () => {
    if (!window.confirm("Se generará un archivo ZIP con TODA la base de datos (contribuyentes, liquidaciones, pagos, usuarios, etc.). ¿Continuar?")) return;
    setRunningBackup(true);
    try {
      const r = await axios.get(`${API}/configuracion/backup`, { ...auth, responseType: "blob" });
      const disp = r.headers["content-disposition"] || "";
      const match = disp.match(/filename="?([^";]+)"?/i);
      const filename = match ? match[1] : `sisaur_backup_${new Date().toISOString().slice(0, 19).replace(/[:T]/g, "").replace(/-/g, "")}.zip`;
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url; a.download = filename; a.click();
      URL.revokeObjectURL(url);
      setUltimoBackup({ filename, size: r.data.size, at: new Date().toISOString() });
      toast.success(`Respaldo descargado: ${filename}`);
    } catch (e) {
      toast.error(e.response?.status === 403
        ? "Solo el Superadmin puede descargar el respaldo."
        : "No se pudo generar el respaldo. Intente de nuevo.");
    } finally { setRunningBackup(false); }
  };

  const loadHistorial = async () => {
    setLoadingHist(true);
    try {
      const params = { limit: 50 };
      if (filtroTipo) params.tipo = filtroTipo;
      if (filtroDesde) params.desde = filtroDesde;
      if (filtroHasta) params.hasta = filtroHasta;
      const r = await axios.get(`${API}/acciones-masivas/logs`, { ...auth, params });
      setHistorial(r.data || []);
    } catch { setHistorial([]); }
    setLoadingHist(false);
  };

  useEffect(() => {
    axios.get(`${API}/configuracion`, auth).then(r => setForm(r.data));
    // Cargar historial de acciones masivas para admins
    axios.get(`${API}/acciones-masivas/logs`, { ...auth, params: { limit: 50 } })
      .then(r => setHistorial(r.data || []))
      .catch(() => setHistorial([]));
    loadScheduleInfo();
  // eslint-disable-next-line
  }, []);
  // Auto-refresh al cambiar filtros del historial
  useEffect(() => {
    const t = setTimeout(loadHistorial, 200);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [filtroTipo, filtroDesde, filtroHasta]);

  const save = async () => {
    try {
      await axios.put(`${API}/configuracion`, form, auth);
      toast.success("Configuración guardada");
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const previewLiquidacionLAE = async () => {
    setRunningMasiva(true);
    try {
      const r = await axios.post(`${API}/acciones-masivas/liquidacion-general-lae`, null, {
        ...auth, params: { dry_run: true },
      });
      setResultado(r.data);
      setModoResultado("preview");
      setShowResultado(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo simular la liquidación general");
    } finally { setRunningMasiva(false); }
  };

  const ejecutarFinal = async () => {
    setRunningMasiva(true);
    try {
      const r = await axios.post(`${API}/acciones-masivas/liquidacion-general-lae`, null, {
        ...auth, params: { dry_run: false },
      });
      setResultado(r.data);
      setModoResultado("final");
      const s = r.data.stats;
      toast.success(`${s.declaraciones_creadas} liquidación(es) creada(s) · ${fmtBs(s.monto_total_bs)}`);
      loadHistorial();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al ejecutar");
    } finally { setRunningMasiva(false); }
  };

  const previewActualizarTasa = async () => {
    setRunningTasa(true);
    try {
      const params = { dry_run: true };
      if (fechaTasa) params.fecha_ref = fechaTasa;
      const r = await axios.post(`${API}/acciones-masivas/actualizar-tasa-liquidacion`, null, { ...auth, params });
      setResTasa(r.data);
      setModoTasa("preview");
      setShowTasa(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo simular la actualización de tasa");
    } finally { setRunningTasa(false); }
  };

  const ejecutarActualizarTasa = async () => {
    setRunningTasa(true);
    try {
      const params = { dry_run: false };
      if (fechaTasa) params.fecha_ref = fechaTasa;
      const r = await axios.post(`${API}/acciones-masivas/actualizar-tasa-liquidacion`, null, { ...auth, params });
      setResTasa(r.data);
      setModoTasa("final");
      const s = r.data.stats;
      toast.success(`${s.liquidaciones_afectadas} liquidación(es) recalculada(s) · Δ ${fmtBs(s.delta_bs)}`);
      loadHistorial();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al ejecutar");
    } finally { setRunningTasa(false); }
  };

  const exportarCSV = () => {
    if (!resultado?.detalle) return;
    const filas = [
      ["Contribuyente", "RIF", "N° LAE", "Últ. pago", "Períodos", "Cant. meses", "Tarifa mensual", "Monto total", "Nº Planilla", "Estatus", "Motivo"],
      ...resultado.detalle.map(r => [
        (r.nombre || "").replace(/"/g, '""'),
        r.rif || "",
        r.numero_lae || "",
        r.ultimo_periodo_pagado || "-",
        (r.periodos_pendientes || []).join(" | "),
        (r.periodos_pendientes || []).length,
        r.tarifa_mensual || 0,
        r.monto_total || 0,
        r.nro_planilla || "-",
        statusMeta[r.estatus]?.label || r.estatus || "-",
        (r.razon || "").replace(/"/g, '""'),
      ]),
    ];
    const csv = filas.map(row => row.map(v => `"${v}"`).join(",")).join("\n");
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `liquidacion-general-lae-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!form) return <div className="text-green-700">Cargando…</div>;
  const upd = (k) => (e) => setForm({ ...form, [k]: e.target?.value !== undefined ? e.target.value : e });
  const puedeMasiva = user?.role === "superadmin" || user?.role === "admin_financiero";

  return (
    <div className="space-y-6" data-testid="configuracion-page">
      <div>
        <h1 className="font-display font-extrabold text-3xl text-green-900 flex items-center gap-3"><Settings className="w-8 h-8 text-orange-500" />Configuración del Sistema</h1>
        <p className="text-green-700/70 mt-1">Datos institucionales, parámetros generales y acciones masivas</p>
      </div>

      <Card className="p-6 border-green-100">
        <h3 className="font-display font-bold text-lg text-green-900 mb-4">Datos Institucionales</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div><Label>Nombre de la Alcaldía</Label><Input data-testid="conf-nombre" value={form.nombre_alcaldia||""} onChange={upd("nombre_alcaldia")} /></div>
          <div><Label>Eslogan</Label><Input data-testid="conf-eslogan" value={form.eslogan||""} onChange={upd("eslogan")} /></div>
          <div><Label>RIF</Label><Input value={form.rif_alcaldia||""} onChange={upd("rif_alcaldia")} /></div>
          <div><Label>Alcalde</Label><Input value={form.nombre_alcalde||""} onChange={upd("nombre_alcalde")} /></div>
          <div className="md:col-span-2"><Label>Dirección</Label><Input value={form.direccion||""} onChange={upd("direccion")} /></div>
          <div><Label>Teléfono</Label><Input value={form.telefono||""} onChange={upd("telefono")} /></div>
          <div><Label>WhatsApp</Label><Input data-testid="conf-whatsapp" value={form.whatsapp||""} onChange={upd("whatsapp")} placeholder="+58 0414-1234567" /></div>
          <div className="md:col-span-2"><Label>Email de contacto</Label><Input type="email" value={form.email_contacto||""} onChange={upd("email_contacto")} /></div>
        </div>
      </Card>

      <Card className="p-6 border-green-100">
        <h3 className="font-display font-bold text-lg text-green-900 mb-4">Parámetros</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div><Label>Moneda</Label><Input value={form.moneda||""} onChange={upd("moneda")} /></div>
          <div><Label>% Multa por mora</Label><Input type="number" step="0.1" value={form.valor_multa_porcentaje||0} onChange={e => setForm({ ...form, valor_multa_porcentaje: Number(e.target.value)||0 })} /></div>
          <div><Label>Día tope sin multa</Label><Input type="number" value={form.fecha_tope_dia||15} onChange={e => setForm({ ...form, fecha_tope_dia: Number(e.target.value)||15 })} /></div>
        </div>
      </Card>

      <div className="flex justify-end">
        <Button data-testid="btn-save-config" onClick={save} className="bg-orange-500 hover:bg-orange-600 text-white px-6">Guardar cambios</Button>
      </div>

      {/* ============== RESPALDO DE BASE DE DATOS ============== */}
      {hasPermission("mantenimiento.backup") && (
        <Card className="p-6 border-2 border-blue-200 bg-gradient-to-br from-blue-50/40 to-white" data-testid="respaldo-card">
          <div className="flex items-start gap-3 mb-4">
            <div className="w-11 h-11 rounded-xl bg-blue-700 flex items-center justify-center shrink-0 shadow-lg">
              <DatabaseBackup className="w-6 h-6 text-white" />
            </div>
            <div className="flex-1">
              <h3 className="font-display font-extrabold text-xl text-green-900">Respaldo de Base de Datos</h3>
              <p className="text-sm text-green-800/70 mt-0.5">
                Descargue manualmente o consulte el respaldo automático semanal.
              </p>
            </div>
            <Button variant="ghost" size="sm" onClick={loadScheduleInfo} disabled={loadingSchedule} data-testid="btn-refresh-schedule">
              <RefreshCw className={`w-4 h-4 ${loadingSchedule ? "animate-spin" : ""}`} />
            </Button>
          </div>

          {/* Descarga manual */}
          <div className="p-5 rounded-2xl border border-blue-200 bg-white mb-4">
            <div className="flex flex-wrap justify-between items-start gap-4">
              <div className="max-w-2xl">
                <p className="text-[10px] font-bold uppercase tracking-widest text-blue-700 mb-1">Descarga manual</p>
                <h4 className="font-display font-bold text-lg text-green-900">Descargar respaldo ahora</h4>
                <p className="text-sm text-slate-700 mt-1">
                  Genera un ZIP con <strong>todas las colecciones</strong> del sistema en formato JSON y <strong>todos los archivos subidos</strong> por usuarios (PDFs, comprobantes, imágenes).
                </p>
                <ul className="mt-3 text-xs text-slate-600 space-y-1 list-disc pl-5">
                  <li><code className="bg-slate-100 px-1 rounded text-[10px]">collections/</code>: JSON de cada colección de Mongo.</li>
                  <li><code className="bg-slate-100 px-1 rounded text-[10px]">uploads/</code>: PDFs de registro público, comprobantes de pago, extractos bancarios.</li>
                  <li><code className="bg-slate-100 px-1 rounded text-[10px]">uploads_manifest.json</code>: índice detallado de todos los archivos incluidos.</li>
                  <li><code className="bg-slate-100 px-1 rounded text-[10px]">manifest.json</code> con metadatos, conteos y agregación por carpeta/extensión.</li>
                  <li><code className="bg-slate-100 px-1 rounded text-[10px]">README.txt</code> con instrucciones de restauración.</li>
                  <li>La operación queda en el log de mantenimiento.</li>
                </ul>
                <div className="mt-3 flex items-start gap-2 p-2.5 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-900">
                  <ShieldAlert className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <span><strong>Confidencial:</strong> este archivo contiene datos personales y financieros.</span>
                </div>
                {ultimoBackup && (
                  <p className="mt-3 text-xs text-green-700 flex items-center gap-1">
                    <CheckCircle2 className="w-3.5 h-3.5" />
                    Último respaldo descargado: <strong>{ultimoBackup.filename}</strong>
                    {ultimoBackup.size ? ` · ${(ultimoBackup.size / 1024).toFixed(0)} KB` : ""}
                  </p>
                )}
              </div>
              <Button onClick={descargarBackup} disabled={runningBackup}
                className="bg-blue-700 hover:bg-blue-800 text-white font-bold shadow-lg" data-testid="btn-descargar-respaldo">
                {runningBackup
                  ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Generando…</>
                  : <><DatabaseBackup className="w-4 h-4 mr-2" />Descargar respaldo (.zip)</>}
              </Button>
            </div>
          </div>

          {/* Scheduler automático */}
          <div className="p-5 rounded-2xl border-2 border-green-300 bg-gradient-to-br from-green-50/70 to-white" data-testid="scheduler-card">
            <div className="flex flex-wrap justify-between items-start gap-4 mb-3">
              <div>
                <p className="text-[10px] font-bold uppercase tracking-widest text-green-700 mb-1 flex items-center gap-1">
                  <Clock className="w-3 h-3" /> Automático
                </p>
                <h4 className="font-display font-bold text-lg text-green-900">Respaldo programado semanal</h4>
                <p className="text-sm text-slate-700 mt-1">
                  Cada {scheduleInfo?.interval_days || 7} día(s), el sistema genera un respaldo, lo guarda en el servidor y lo envía por email a los Superadmin.
                </p>
              </div>
              <Button onClick={dispararBackupProgramado} disabled={runningScheduled}
                className="bg-green-700 hover:bg-green-800 text-white font-bold shadow" data-testid="btn-run-scheduled-backup">
                {runningScheduled
                  ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Ejecutando…</>
                  : <><PlayCircle className="w-4 h-4 mr-2" />Ejecutar ahora (guarda + email)</>}
              </Button>
            </div>

            {scheduleInfo ? (
              <>
                {/* Config y destinatarios */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 my-4">
                  <div className="rounded-lg bg-white border border-green-100 p-3">
                    <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Frecuencia</div>
                    <div className="font-display font-bold text-green-900 mt-0.5">Cada {scheduleInfo.interval_days} d</div>
                  </div>
                  <div className="rounded-lg bg-white border border-green-100 p-3">
                    <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Se conservan</div>
                    <div className="font-display font-bold text-green-900 mt-0.5">{scheduleInfo.keep_count} respaldos</div>
                  </div>
                  <div className="rounded-lg bg-white border border-green-100 p-3">
                    <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Email</div>
                    <div className={`font-display font-bold mt-0.5 ${scheduleInfo.email_enabled ? "text-green-700" : "text-amber-700"}`}>
                      {scheduleInfo.email_enabled ? "Habilitado" : "Deshabilitado"}
                    </div>
                  </div>
                  <div className="rounded-lg bg-white border border-green-100 p-3">
                    <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Destinatarios</div>
                    <div className="font-display font-bold text-green-900 mt-0.5">{scheduleInfo.email_recipients?.length || 0}</div>
                  </div>
                </div>

                {!scheduleInfo.email_enabled && (
                  <div className="flex items-start gap-2 p-2.5 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-900 mb-3">
                    <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                    <span><strong>Resend no configurado.</strong> El respaldo se guardará en el servidor pero NO se enviará por email hasta configurar <code>RESEND_API_KEY</code> en el .env.</span>
                  </div>
                )}

                <div className="mb-3">
                  <div className="text-xs text-slate-600 mb-1">Se envía por email a:</div>
                  <div className="flex flex-wrap gap-1.5">
                    {(scheduleInfo.email_recipients || []).length === 0 && (
                      <span className="text-xs text-slate-500 italic">(ningún superadmin con email)</span>
                    )}
                    {(scheduleInfo.email_recipients || []).map(r => (
                      <span key={r.email} className="inline-flex items-center gap-1 text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full font-mono" data-testid={`recipient-${r.email}`}>
                        <Mail className="w-3 h-3" /> {r.email}
                      </span>
                    ))}
                  </div>
                </div>

                {scheduleInfo.last_run && (
                  <div className="text-xs text-slate-700 bg-slate-50 border border-slate-200 rounded-lg p-2.5 mb-3">
                    <strong>Última ejecución automática:</strong>{" "}
                    {new Date(scheduleInfo.last_run.timestamp).toLocaleString("es-VE")} ·{" "}
                    <span className={scheduleInfo.last_run.status === "ok" ? "text-green-700" : "text-red-700"}>
                      {scheduleInfo.last_run.status === "ok" ? "✅ OK" : "❌ Error"}
                    </span>
                    {scheduleInfo.last_run.email_status && (
                      <> · Email: <span className="font-mono">{scheduleInfo.last_run.email_status}</span></>
                    )}
                  </div>
                )}

                {/* Respaldos almacenados */}
                <div className="mt-4">
                  <div className="text-sm font-bold text-green-900 mb-2 flex items-center gap-1">
                    <FileText className="w-4 h-4" /> Respaldos almacenados en el servidor ({scheduleInfo.stored_backups?.length || 0})
                  </div>
                  {(scheduleInfo.stored_backups || []).length === 0 ? (
                    <div className="text-xs text-slate-500 italic py-3 text-center border border-dashed border-slate-200 rounded-lg">
                      Aún no hay respaldos en <code>{scheduleInfo.backup_dir}</code>. Se generará automáticamente en la próxima ejecución programada.
                    </div>
                  ) : (
                    <div className="overflow-x-auto border border-green-100 rounded-lg bg-white">
                      <table className="w-full text-sm" data-testid="stored-backups-table">
                        <thead className="bg-green-50">
                          <tr>
                            <th className="text-left p-2 text-xs text-green-900">Archivo</th>
                            <th className="text-left p-2 text-xs text-green-900">Fecha</th>
                            <th className="text-right p-2 text-xs text-green-900">Tamaño</th>
                            <th className="text-right p-2 text-xs text-green-900 w-32">Acciones</th>
                          </tr>
                        </thead>
                        <tbody>
                          {scheduleInfo.stored_backups.map(b => (
                            <tr key={b.filename} className="border-t border-green-50" data-testid={`stored-row-${b.filename}`}>
                              <td className="p-2 font-mono text-xs">{b.filename}</td>
                              <td className="p-2 text-xs text-slate-600">{new Date(b.mtime).toLocaleString("es-VE")}</td>
                              <td className="p-2 text-xs text-right font-mono">{(b.size_bytes / 1024).toFixed(0)} KB</td>
                              <td className="p-2 text-right">
                                <Button size="sm" variant="outline" onClick={() => descargarStored(b.filename)}
                                        data-testid={`btn-download-${b.filename}`} className="mr-1">
                                  <Download className="w-3.5 h-3.5" />
                                </Button>
                                <Button size="sm" variant="outline" onClick={() => eliminarStored(b.filename)}
                                        data-testid={`btn-delete-${b.filename}`}
                                        className="text-red-600 border-red-200 hover:bg-red-50">
                                  <Trash2 className="w-3.5 h-3.5" />
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <div className="text-center py-4 text-sm text-slate-500">
                {loadingSchedule ? "Cargando configuración del scheduler…" : "No se pudo cargar la información del scheduler."}
              </div>
            )}
          </div>
        </Card>
      )}

      {/* ============== ACCIONES MASIVAS ============== */}
      {puedeMasiva && (
        <Card className="p-6 border-2 border-orange-200 bg-gradient-to-br from-orange-50/40 to-white" data-testid="acciones-masivas-card">
          <div className="flex items-start gap-3 mb-4">
            <div className="w-11 h-11 rounded-xl bg-orange-500 flex items-center justify-center shrink-0 shadow-lg">
              <Zap className="w-6 h-6 text-white" />
            </div>
            <div>
              <h3 className="font-display font-extrabold text-xl text-green-900">Acciones masivas</h3>
              <p className="text-sm text-green-800/70 mt-0.5">
                Procesos que afectan a muchos contribuyentes de una sola ejecución. Úselas con precaución.
              </p>
            </div>
          </div>

          <div className="p-5 rounded-2xl border border-orange-200 bg-white">
            <div className="flex flex-wrap justify-between items-start gap-4">
              <div className="max-w-xl">
                <p className="text-[10px] font-bold uppercase tracking-widest text-orange-600 mb-1">Generación automática</p>
                <h4 className="font-display font-bold text-lg text-green-900">Liquidación General con LAE</h4>
                <p className="text-sm text-slate-700 mt-1">
                  Genera automáticamente una liquidación a <strong>cada contribuyente activo con LAE</strong>,
                  cubriendo <strong>todos los meses pendientes</strong> desde el último pago hasta el mes en curso.
                  Al pagarla, el contribuyente quedará solvente.
                </p>
                <ul className="mt-3 text-xs text-slate-600 space-y-1 list-disc pl-5">
                  <li>Aplica tarifa vigente por clasificación e inmueble.</li>
                  <li>Anula declaraciones &quot;Registrada&quot; existentes que cubran los mismos períodos.</li>
                  <li>Requiere previsualizar antes de ejecutar.</li>
                </ul>
              </div>
              <Button
                onClick={previewLiquidacionLAE}
                disabled={runningMasiva}
                className="bg-orange-500 hover:bg-orange-600 text-white font-bold shadow-lg"
                data-testid="btn-preview-lae"
              >
                {runningMasiva
                  ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Simulando…</>
                  : <><Zap className="w-4 h-4 mr-2" />Previsualizar liquidación</>}
              </Button>
            </div>
          </div>

          {/* --- Acción masiva 2: Actualización de Tasa Liquidación --- */}
          <div className="p-5 rounded-2xl border border-green-200 bg-white mt-4" data-testid="card-actualizar-tasa">
            <div className="flex flex-wrap justify-between items-start gap-4">
              <div className="max-w-xl">
                <p className="text-[10px] font-bold uppercase tracking-widest text-green-700 mb-1">Recálculo por cotización</p>
                <h4 className="font-display font-bold text-lg text-green-900 flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-green-700" /> Actualización de Tasa Liquidación
                </h4>
                <p className="text-sm text-slate-700 mt-1">
                  Aplica el <strong>TCMMV vigente</strong> a todas las liquidaciones existentes con
                  estatus <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100 mx-1">Registrada</Badge>
                  y <Badge className="bg-orange-100 text-orange-800 hover:bg-orange-100">Sin pagar</Badge>,
                  recalculando el <strong>TOTAL en Bolívares</strong> y las equivalencias en USD/EUR.
                </p>
                <ul className="mt-3 text-xs text-slate-600 space-y-1 list-disc pl-5">
                  <li>Solo afecta líneas <strong>automáticas</strong> (AUTO); los overrides manuales se preservan.</li>
                  <li>Las planillas ya pagadas o anuladas <strong>no</strong> se tocan.</li>
                  <li>Se aplica la cotización <strong>vigente en la fecha seleccionada</strong>.</li>
                  <li>Requiere previsualizar antes de ejecutar.</li>
                </ul>
              </div>
              <div className="flex flex-col items-end gap-2">
                <div className="w-56">
                  <Label className="text-xs text-slate-600">Fecha de referencia</Label>
                  <Input
                    type="date"
                    value={fechaTasa}
                    onChange={e => setFechaTasa(e.target.value)}
                    max={new Date().toISOString().slice(0, 10)}
                    className="h-9"
                    data-testid="fecha-tasa-input"
                  />
                  <p className="text-[10px] text-slate-500 mt-1">Usa el TCMMV vigente al día indicado</p>
                </div>
                <Button
                  onClick={previewActualizarTasa}
                  disabled={runningTasa}
                  className="bg-green-700 hover:bg-green-800 text-white font-bold shadow-lg w-56"
                  data-testid="btn-preview-tasa"
                >
                  {runningTasa
                    ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Simulando…</>
                    : <><RefreshCw className="w-4 h-4 mr-2" />Previsualizar actualización</>}
                </Button>
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* ===== Historial de acciones masivas ===== */}
      {puedeMasiva && (
        <Card className="p-6 border border-green-200" data-testid="historial-masivas-card">
          <div className="flex items-start justify-between gap-3 mb-4">
            <div className="flex items-start gap-3">
              <div className="w-11 h-11 rounded-xl bg-green-700 flex items-center justify-center shrink-0 shadow">
                <History className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="font-display font-extrabold text-xl text-green-900">Historial de acciones masivas</h3>
                <p className="text-sm text-green-800/70 mt-0.5">Registro de todas las ejecuciones (simulaciones y reales) con exportación auditable.</p>
              </div>
            </div>
            <Button variant="outline" size="sm" onClick={loadHistorial} disabled={loadingHist} data-testid="btn-refresh-historial">
              <RefreshCw className={`w-4 h-4 mr-1 ${loadingHist ? "animate-spin" : ""}`} /> Actualizar
            </Button>
          </div>
          {/* Filtros: Tipo + Rango de fechas */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-2 mb-3">
            <div>
              <Label className="text-xs text-slate-600">Tipo</Label>
              <Select value={filtroTipo || "all"} onValueChange={v => setFiltroTipo(v === "all" ? "" : v)}>
                <SelectTrigger className="h-9" data-testid="filtro-tipo-historial"><SelectValue placeholder="Todos" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos los tipos</SelectItem>
                  <SelectItem value="liquidacion_general_lae">Liquidación General LAE</SelectItem>
                  <SelectItem value="actualizacion_tasa_liquidacion">Actualización de Tasa</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-slate-600">Desde</Label>
              <Input type="date" value={filtroDesde} onChange={e => setFiltroDesde(e.target.value)} className="h-9" data-testid="filtro-desde-historial" />
            </div>
            <div>
              <Label className="text-xs text-slate-600">Hasta</Label>
              <Input type="date" value={filtroHasta} onChange={e => setFiltroHasta(e.target.value)} className="h-9" data-testid="filtro-hasta-historial" />
            </div>
            <div className="flex items-end">
              <Button variant="outline" size="sm" onClick={() => { setFiltroTipo(""); setFiltroDesde(""); setFiltroHasta(""); }} disabled={!filtroTipo && !filtroDesde && !filtroHasta} data-testid="btn-limpiar-filtros-hist" className="w-full h-9">
                Limpiar filtros
              </Button>
            </div>
          </div>
          <div className="overflow-x-auto border border-green-100 rounded-lg">
            <table className="w-full text-sm">
              <thead className="bg-green-50 text-green-900">
                <tr>
                  <th className="text-left p-2.5">Fecha</th>
                  <th className="text-left p-2.5">Tipo</th>
                  <th className="text-left p-2.5">Modo</th>
                  <th className="text-left p-2.5">Ejecutado por</th>
                  <th className="text-right p-2.5">Afectadas</th>
                  <th className="text-right p-2.5">Monto (Bs.)</th>
                  <th className="text-center p-2.5">Descargar</th>
                </tr>
              </thead>
              <tbody>
                {historial.length === 0 && !loadingHist && (
                  <tr><td colSpan={7} className="p-6 text-center text-slate-400">Sin ejecuciones registradas</td></tr>
                )}
                {loadingHist && (
                  <tr><td colSpan={7} className="p-6 text-center text-green-700">Cargando…</td></tr>
                )}
                {historial.map(log => {
                  const isTasa = log.tipo === "actualizacion_tasa_liquidacion";
                  const tipoLbl = isTasa ? "Actualización de Tasa" : (log.tipo === "liquidacion_general_lae" ? "Liquidación General LAE" : log.tipo);
                  const afect = isTasa ? (log.stats?.liquidaciones_afectadas || 0) : (log.stats?.declaraciones_creadas || 0);
                  const monto = isTasa ? (log.stats?.delta_bs || 0) : (log.stats?.monto_total_bs || 0);
                  return (
                    <tr key={log.id} className="border-b border-green-50 hover:bg-green-50/40" data-testid={`log-row-${log.id.slice(0,8)}`}>
                      <td className="p-2.5 font-mono text-xs">{(log.timestamp || "").replace("T", " ").slice(0, 19)}</td>
                      <td className="p-2.5">
                        {isTasa
                          ? <span className="inline-flex items-center gap-1 text-green-800"><TrendingUp className="w-3.5 h-3.5" />{tipoLbl}</span>
                          : <span className="inline-flex items-center gap-1 text-orange-700"><Zap className="w-3.5 h-3.5" />{tipoLbl}</span>}
                      </td>
                      <td className="p-2.5">
                        {log.dry_run
                          ? <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100">Simulación</Badge>
                          : <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100">Ejecutada</Badge>}
                      </td>
                      <td className="p-2.5 text-slate-600 text-xs">{log.disparado_por || "-"}</td>
                      <td className="p-2.5 text-right font-mono">{afect}</td>
                      <td className="p-2.5 text-right font-mono font-bold text-green-800">{monto > 0 ? "+" : monto < 0 ? "" : ""}{fmtBs(monto)}</td>
                      <td className="p-2.5">
                        <div className="flex items-center justify-center gap-1">
                          <a href={`${API}/acciones-masivas/logs/${log.id}/excel`} target="_blank" rel="noopener noreferrer" download
                            onClick={async (e) => {
                              e.preventDefault();
                              try {
                                const r = await axios.get(`${API}/acciones-masivas/logs/${log.id}/excel`, { ...auth, responseType: "blob" });
                                const url = URL.createObjectURL(r.data);
                                const a = document.createElement("a"); a.href = url;
                                a.download = `accion-masiva-${log.tipo}-${log.id.slice(0,8)}.xlsx`; a.click();
                                URL.revokeObjectURL(url);
                              } catch { toast.error("Error al descargar Excel"); }
                            }}
                            data-testid={`log-excel-${log.id.slice(0,8)}`}
                            className="inline-flex items-center gap-1 px-2 py-1 rounded bg-green-100 hover:bg-green-200 text-green-800 text-xs font-medium">
                            <FileSpreadsheet className="w-3.5 h-3.5" /> Excel
                          </a>
                          <a href={`${API}/acciones-masivas/logs/${log.id}/pdf`} target="_blank" rel="noopener noreferrer"
                            onClick={async (e) => {
                              e.preventDefault();
                              try {
                                const r = await axios.get(`${API}/acciones-masivas/logs/${log.id}/pdf`, { ...auth, responseType: "blob" });
                                const url = URL.createObjectURL(r.data);
                                window.open(url, "_blank");
                              } catch { toast.error("Error al descargar PDF"); }
                            }}
                            data-testid={`log-pdf-${log.id.slice(0,8)}`}
                            className="inline-flex items-center gap-1 px-2 py-1 rounded bg-orange-100 hover:bg-orange-200 text-orange-800 text-xs font-medium">
                            <FileText className="w-3.5 h-3.5" /> PDF
                          </a>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Modal resultado */}
      <Dialog open={showResultado} onOpenChange={setShowResultado}>
        <DialogContent className="max-w-6xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {modoResultado === "preview"
                ? <><AlertTriangle className="w-5 h-5 text-orange-600" /> Previsualización · Liquidación General con LAE</>
                : <><CheckCircle2 className="w-5 h-5 text-emerald-600" /> Ejecución completada</>}
            </DialogTitle>
            <DialogDescription>
              {modoResultado === "preview"
                ? "Revise los datos antes de confirmar. Ninguna declaración fue creada aún."
                : "Todas las declaraciones fueron generadas y persistidas en la base de datos."}
            </DialogDescription>
          </DialogHeader>
          {resultado && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {[
                  { k: "contribuyentes_evaluados", label: "Contrib. evaluados", color: "text-slate-900", bg: "bg-slate-50" },
                  { k: "contribuyentes_facturados", label: "A facturar", color: "text-orange-800", bg: "bg-orange-50" },
                  { k: "declaraciones_creadas", label: modoResultado === "preview" ? "Se crearán" : "Creadas", color: "text-emerald-800", bg: "bg-emerald-50" },
                  { k: "monto_total_bs", label: "Monto total (Bs.)", color: "text-green-900", bg: "bg-green-50", isMoney: true },
                ].map(m => (
                  <div key={m.k} className={`p-4 rounded-xl ${m.bg} border border-slate-200`}>
                    <p className="text-[10px] font-bold uppercase tracking-widest text-slate-600">{m.label}</p>
                    <p className={`text-2xl font-display font-extrabold mt-1 ${m.color}`}>
                      {m.isMoney ? fmtBs(resultado.stats[m.k]) : resultado.stats[m.k]}
                    </p>
                  </div>
                ))}
              </div>

              {(resultado.stats.contribuyentes_al_dia > 0 || resultado.stats.contribuyentes_sin_tarifa > 0 || resultado.stats.declaraciones_anuladas > 0) && (
                <div className="flex flex-wrap gap-2 text-xs">
                  {resultado.stats.contribuyentes_al_dia > 0 && <Badge className="bg-green-100 text-green-800">{resultado.stats.contribuyentes_al_dia} al día</Badge>}
                  {resultado.stats.contribuyentes_sin_tarifa > 0 && <Badge className="bg-amber-100 text-amber-800">{resultado.stats.contribuyentes_sin_tarifa} sin tarifa</Badge>}
                  {resultado.stats.declaraciones_anuladas > 0 && <Badge className="bg-rose-100 text-rose-800">{resultado.stats.declaraciones_anuladas} anuladas previas</Badge>}
                  {resultado.stats.contribuyentes_error > 0 && <Badge className="bg-rose-100 text-rose-800">{resultado.stats.contribuyentes_error} errores</Badge>}
                </div>
              )}

              <div className="border border-green-100 rounded-lg overflow-hidden">
                <div className="max-h-[400px] overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-green-50 text-green-900 sticky top-0">
                      <tr>
                        <th className="text-left p-2">Contribuyente</th>
                        <th className="text-left p-2">LAE</th>
                        <th className="text-center p-2">Últ. pago</th>
                        <th className="text-center p-2">Meses</th>
                        <th className="text-right p-2">Tarifa/mes</th>
                        <th className="text-right p-2">Total</th>
                        <th className="text-left p-2">Planilla</th>
                        <th className="text-center p-2">Estado</th>
                      </tr>
                    </thead>
                    <tbody>
                      {resultado.detalle.map((r, i) => {
                        const meta = statusMeta[r.estatus] || { color: "bg-slate-100", label: r.estatus };
                        return (
                          <tr key={i} className="border-b border-green-50" data-testid={`lae-row-${i}`}>
                            <td className="p-2">
                              <p className="font-semibold text-green-900 text-xs">{r.nombre}</p>
                              <p className="text-xs text-slate-500 font-mono">{r.rif}</p>
                            </td>
                            <td className="p-2 font-mono text-xs">{r.numero_lae}</td>
                            <td className="p-2 text-center text-xs">{r.ultimo_periodo_pagado || "—"}</td>
                            <td className="p-2 text-center font-mono">{(r.periodos_pendientes || []).length}</td>
                            <td className="p-2 text-right font-mono text-xs">{fmtBs(r.tarifa_mensual)}</td>
                            <td className="p-2 text-right font-bold text-orange-700">{fmtBs(r.monto_total)}</td>
                            <td className="p-2 font-mono text-xs">{r.nro_planilla || "—"}</td>
                            <td className="p-2 text-center">
                              <Badge className={`${meta.color} hover:${meta.color}`}>{meta.label}</Badge>
                              {r.razon && <p className="text-[10px] text-slate-500 mt-1">{r.razon}</p>}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
          <DialogFooter className="flex-wrap gap-2">
            <Button variant="ghost" onClick={exportarCSV} data-testid="btn-export-csv"><Download className="w-4 h-4 mr-2" />Exportar CSV</Button>
            <Button variant="ghost" onClick={() => setShowResultado(false)} data-testid="btn-close-modal">Cerrar</Button>
            {modoResultado === "preview" && resultado?.stats?.contribuyentes_facturados > 0 && (
              <Button
                onClick={() => {
                  if (window.confirm(`Se crearán ${resultado.stats.contribuyentes_facturados} liquidaciones por un total de ${fmtBs(resultado.stats.monto_total_bs)}. ¿Confirmar?`)) {
                    ejecutarFinal();
                  }
                }}
                disabled={runningMasiva}
                className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold"
                data-testid="btn-ejecutar-final"
              >
                <PlayCircle className="w-4 h-4 mr-2" />
                {runningMasiva ? "Ejecutando..." : "Confirmar y ejecutar"}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ===== Modal: Actualización de Tasa Liquidación ===== */}
      <Dialog open={showTasa} onOpenChange={setShowTasa}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto" data-testid="dialog-tasa">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {modoTasa === "preview"
                ? <><AlertTriangle className="w-5 h-5 text-orange-600" /> Previsualización · Actualización de Tasa</>
                : <><CheckCircle2 className="w-5 h-5 text-emerald-600" /> Recálculo aplicado</>}
            </DialogTitle>
            <DialogDescription>
              {modoTasa === "preview"
                ? "Estas son las liquidaciones que serán recalculadas al aplicar el TCMMV vigente. Ningún cambio se ha persistido."
                : "El TCMMV vigente se aplicó a las liquidaciones y los totales fueron actualizados."}
            </DialogDescription>
          </DialogHeader>
          {resTasa && (
            <div className="space-y-4">
              {/* Cotización aplicada */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-xs bg-green-50 rounded-lg p-3 border border-green-100">
                <div><span className="text-green-700/70">Fecha de ref:</span><br/><b className="text-green-900" data-testid="tasa-fecha-ref">{resTasa.fecha_ref || "hoy"}</b></div>
                <div><span className="text-green-700/70">Fecha cotización:</span><br/><b className="text-green-900">{(resTasa.cotizacion_aplicada?.fecha_cot || "-").slice(0,10)}</b></div>
                <div><span className="text-green-700/70">TCMMV:</span><br/><b className="text-green-900 font-mono">{fmtBs(resTasa.cotizacion_aplicada?.tcmmv)}</b></div>
                <div><span className="text-green-700/70">Bs./USD:</span><br/><b className="text-green-900 font-mono">{fmtBs(resTasa.cotizacion_aplicada?.dolar)}</b></div>
                <div><span className="text-green-700/70">Bs./EUR:</span><br/><b className="text-green-900 font-mono">{fmtBs(resTasa.cotizacion_aplicada?.euro)}</b></div>
              </div>

              {/* KPIs */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {[
                  { k: "liquidaciones_evaluadas", label: "Evaluadas", color: "text-slate-900", bg: "bg-slate-50" },
                  { k: "liquidaciones_afectadas", label: modoTasa === "preview" ? "Serán afectadas" : "Afectadas", color: "text-emerald-800", bg: "bg-emerald-50" },
                  { k: "liquidaciones_sin_cambio", label: "Sin cambio", color: "text-slate-700", bg: "bg-slate-50" },
                  { k: "delta_bs", label: "Δ Total (Bs.)", color: "text-orange-800", bg: "bg-orange-50", isMoney: true },
                ].map(m => (
                  <div key={m.k} className={`p-4 rounded-xl ${m.bg} border border-slate-200`}>
                    <p className="text-[10px] font-bold uppercase tracking-widest text-slate-600">{m.label}</p>
                    <p className={`text-2xl font-display font-extrabold mt-1 ${m.color}`}>
                      {m.isMoney ? fmtBs(resTasa.stats[m.k]) : resTasa.stats[m.k]}
                    </p>
                  </div>
                ))}
              </div>

              {/* Antes vs después */}
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="p-3 rounded-lg bg-slate-50 border border-slate-200">
                  <p className="text-[10px] uppercase font-bold text-slate-500">Monto total anterior</p>
                  <p className="text-xl font-bold text-slate-800 font-mono">{fmtBs(resTasa.stats.monto_total_anterior_bs)}</p>
                </div>
                <div className="p-3 rounded-lg bg-green-50 border border-green-200">
                  <p className="text-[10px] uppercase font-bold text-green-700">Monto total nuevo</p>
                  <p className="text-xl font-bold text-green-800 font-mono">{fmtBs(resTasa.stats.monto_total_nuevo_bs)}</p>
                </div>
              </div>

              {/* Tabla detalle */}
              {resTasa.detalle?.length > 0 && (
                <div className="border border-green-100 rounded-lg overflow-hidden">
                  <div className="max-h-[400px] overflow-y-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-green-50 text-green-900 sticky top-0"><tr>
                        <th className="text-left p-2">Nº Planilla</th>
                        <th className="text-left p-2">Contribuyente</th>
                        <th className="text-right p-2">Total anterior</th>
                        <th className="text-right p-2">Total nuevo</th>
                        <th className="text-right p-2">Δ Bs.</th>
                        <th className="text-right p-2">USD nuevo</th>
                        <th className="text-center p-2">Líneas</th>
                      </tr></thead>
                      <tbody>
                        {resTasa.detalle.map((r, i) => (
                          <tr key={i} className="border-b border-green-50 hover:bg-green-50/40">
                            <td className="p-2 font-mono text-xs">{r.nro_planilla || "-"}</td>
                            <td className="p-2">{r.contribuyente || "-"}<br/><span className="text-[10px] text-slate-500 font-mono">{r.rif}</span></td>
                            <td className="p-2 text-right font-mono text-slate-600">{fmtBs(r.total_anterior)}</td>
                            <td className="p-2 text-right font-mono font-bold text-green-800">{fmtBs(r.total_nuevo)}</td>
                            <td className={`p-2 text-right font-mono font-bold ${r.delta_bs > 0 ? "text-orange-700" : r.delta_bs < 0 ? "text-blue-700" : "text-slate-500"}`}>{r.delta_bs > 0 ? "+" : ""}{fmtBs(r.delta_bs)}</td>
                            <td className="p-2 text-right font-mono text-slate-600">{fmtBs(r.divisas_usd_nuevo)}</td>
                            <td className="p-2 text-center text-xs">{r.lineas_recalculadas || 0}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
              {(!resTasa.detalle || !resTasa.detalle.length) && (
                <div className="p-6 rounded-lg bg-emerald-50 text-emerald-800 text-center text-sm border border-emerald-200">
                  🎉 Todas las liquidaciones ya están al día con la cotización vigente. No hay recálculos pendientes.
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTasa(false)} data-testid="btn-close-tasa">Cerrar</Button>
            {modoTasa === "preview" && resTasa?.stats?.liquidaciones_afectadas > 0 && (
              <Button onClick={ejecutarActualizarTasa} disabled={runningTasa} className="bg-green-700 hover:bg-green-800 text-white font-bold" data-testid="btn-confirm-tasa">
                {runningTasa ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Aplicando…</> : <><PlayCircle className="w-4 h-4 mr-2" /> Confirmar y aplicar</>}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
